import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ChartBar,
  Clock,
  MapPinSimple,
  Path,
  Sparkle,
} from "@phosphor-icons/react";

function Stat({ label, value, hint }) {
  return (
    <div className="border border-white/10 bg-[#121212] p-5 relative overflow-hidden">
      <p className="label-micro">{label}</p>
      <p className="text-4xl font-bold mt-2 font-mono text-white tracking-tight" data-testid={`stat-${label}`}>
        {value}
      </p>
      {hint && <p className="text-xs text-white/40 mt-1">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [o, r] = await Promise.all([
          api.get("/stats/overview"),
          api.get("/runs/recent?limit=6"),
        ]);
        setOverview(o.data);
        setRecent(r.data.runs || []);
      } catch (_) {}
    })();
  }, []);

  return (
    <div className="h-full overflow-auto">
      <div
        className="relative border-b border-white/10 px-10 py-12 overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(10,10,10,0.6), rgba(10,10,10,1)), url('https://images.unsplash.com/photo-1704229266209-47d8d6ad0c46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1hcCUyMGRhcmt8ZW58MHx8fHwxNzc4NDkzNDM2fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative max-w-6xl">
          <p className="label-micro text-[#FF9800] mb-3">DASHBOARD</p>
          <h1 className="text-5xl font-bold tracking-tight" data-testid="dashboard-title">
            Tu cuadro de mandos de movilidad.
          </h1>
          <p className="text-white/60 max-w-2xl mt-3">
            Visión rápida de tus consultas, zonas disponibles y últimos análisis
            generados con los datos abiertos del MITMA.
          </p>
          <Link
            to="/analisis"
            data-testid="dashboard-new-analysis-btn"
            className="inline-flex items-center gap-2 mt-6 px-5 py-3 bg-[#FF9800] text-black font-semibold tracking-tight btn-press hover:bg-[#FFA726]"
          >
            <Sparkle weight="bold" size={18} />
            Nuevo análisis
            <ArrowUpRight weight="bold" size={16} />
          </Link>
        </div>
      </div>

      <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Stat label="DISTRITOS" value={overview?.zones_distrito ?? "—"} hint="Disponibles" />
        <Stat label="MUNICIPIOS" value={overview?.zones_municipio ?? "—"} />
        <Stat label="GAU" value={overview?.zones_gau ?? "—"} />
        <Stat label="ANÁLISIS REALIZADOS" value={overview?.runs_count ?? 0} />
      </div>

      <div className="px-10 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-white/10 bg-[#121212]">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <p className="label-micro">HISTORIAL RECIENTE</p>
            <Clock className="text-white/40" size={16} />
          </div>
          <div className="divide-y divide-white/10" data-testid="recent-runs-list">
            {recent.length === 0 && (
              <div className="px-5 py-8 text-center text-white/40 text-sm">
                Aún no hay análisis. Lanza tu primer análisis para verlo aquí.
              </div>
            )}
            {recent.map((r) => (
              <div key={r.id} className="px-5 py-4 grid grid-cols-12 items-center gap-3 hover:bg-white/[0.02]">
                <p className="col-span-3 font-mono text-xs text-white/60">{r.date}</p>
                <p className="col-span-3 text-sm">
                  <span className="text-white/40">{r.level}</span>{" "}
                  <span className="text-white">
                    · {r.origins.length} × {r.destinations.length}
                  </span>
                </p>
                <p className="col-span-3 text-xs text-white/40 truncate">
                  {r.filters?.purpose !== "all" ? r.filters.purpose : "todos los motivos"}
                </p>
                <p className="col-span-3 text-right font-mono text-sm text-[#00E5FF]">
                  {Number(r.total_trips).toLocaleString("es-ES")} viajes
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212]">
          <div className="px-5 py-4 border-b border-white/10">
            <p className="label-micro">ACERCA DE</p>
          </div>
          <div className="p-5 space-y-4 text-sm text-white/70">
            <p className="flex gap-3">
              <MapPinSimple weight="duotone" size={20} className="text-[#FF9800] shrink-0 mt-0.5" />
              Consulta zonas de Distritos, Municipios y GAU según la zonificación
              del MITMA.
            </p>
            <p className="flex gap-3">
              <Path weight="duotone" size={20} className="text-[#00E5FF] shrink-0 mt-0.5" />
              Genera matrices O/D filtrando por fecha, periodo horario, motivo,
              distancia y modo.
            </p>
            <p className="flex gap-3">
              <ChartBar weight="duotone" size={20} className="text-[#FF5722] shrink-0 mt-0.5" />
              Visualiza flujos, heatmaps y gráficos. Descarga la matriz en CSV.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
